import { Injectable, computed, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Data, Route, Router } from '@angular/router';
import { ROLE_LANDING, Role } from '../../constants/roles';
import { MenuItem, buildMenuFromRoutes, firstMenuRoute } from '../../../shared/constants/menu';
import { SessionService } from '../smartclock/session.service';
import { collectRequirements, satisfiesAll } from './access-rules';
import { AccessService } from './access.service';
import { CatalogLayoutService } from './catalog-layout.service';

/** Destino de toda denegación del guard (nunca otro módulo). */
export const NO_ACCESS_URL = '/app/sin-acceso';

/**
 * Navegación derivada de los permisos: menú lateral, landing por defecto y
 * reevaluación de la ruta actual. Usa las mismas reglas que el guard.
 */
@Injectable({ providedIn: 'root' })
export class AccessNavigationService {
  private readonly router = inject(Router);
  private readonly access = inject(AccessService);
  private readonly session = inject(SessionService);
  private readonly catalogLayout = inject(CatalogLayoutService);

  /**
   * Menú lateral: entradas `data.menu` de Router.config que el usuario puede abrir, agrupadas y
   * nombradas como el catálogo de permisos (estructura editable desde el backoffice).
   */
  readonly menu = computed<MenuItem[]>(() => {
    const snapshot = this.access.snapshot();
    return buildMenuFromRoutes(
      this.router.config,
      (chain) => satisfiesAll(collectRequirements(chain), snapshot),
      this.catalogLayout.layout(),
    );
  });

  /** ¿La cadena de `data` (raíz → ruta) pasa el guard con el acceso actual? */
  canOpenChain(chain: ReadonlyArray<Data | undefined | null>): boolean {
    return satisfiesAll(collectRequirements(chain), this.access.snapshot());
  }

  /** ¿La ruta activada (snapshot) pasa el guard con el acceso actual? */
  canOpenSnapshot(route: ActivatedRouteSnapshot): boolean {
    return this.canOpenChain(route.pathFromRoot.map((r) => r.routeConfig?.data));
  }

  /** ¿La ruta que se está mostrando sigue permitida? */
  isCurrentRouteAllowed(): boolean {
    let leaf = this.router.routerState.snapshot.root;
    while (leaf.firstChild) leaf = leaf.firstChild;
    return this.canOpenSnapshot(leaf);
  }

  /** ¿Una URL de la app (sin query) pasa el guard? Rutas sin configurar → false. */
  canOpenUrl(url: string): boolean {
    const segments = url.split('?')[0].split('/').filter(Boolean);
    const chain = matchRouteChain(this.router.config, segments);
    return chain !== null && this.canOpenChain(chain.map((route) => route.data));
  }

  /**
   * Landing por defecto: el de su rol (ROLE_LANDING) si lo puede abrir; si
   * no, la primera entrada del menú. null → no tiene ningún módulo habilitado
   * (o sus permisos no cargaron).
   */
  landingUrl(): string | null {
    const role = (this.access.role() ?? this.session.role) as Role | null;
    const preferred = role ? ROLE_LANDING[role] : undefined;
    if (preferred && this.canOpenUrl(preferred)) return preferred;
    return firstMenuRoute(this.menu());
  }
}

/**
 * Resuelve una URL estática contra la configuración del router (sin guards ni
 * lazy children): devuelve la cadena de rutas raíz → hoja, o null.
 */
function matchRouteChain(routes: ReadonlyArray<Route>, segments: string[]): Route[] | null {
  for (const route of routes) {
    if (route.redirectTo !== undefined || route.path === '**' || route.path === undefined) {
      continue;
    }
    const own = route.path.split('/').filter(Boolean);
    if (own.length > segments.length) continue;
    const matches = own.every((part, i) => part.startsWith(':') || part === segments[i]);
    if (!matches) continue;
    const rest = segments.slice(own.length);
    if (route.children?.length) {
      const child = matchRouteChain(route.children, rest);
      if (child) return [route, ...child];
      continue;
    }
    if (rest.length === 0) return [route];
  }
  return null;
}
